// לקוח ל-/api/triggers* (19 §1.4) ולשונית "תזמונים" ביצירה (19 §1.5). השרת מחשב describeHe/הפעלה הבאה — המסך מציג.
import { z } from 'zod';
import { authed, json } from './api.ts';

const enc = encodeURIComponent;
const body = (method: string, b: unknown): RequestInit => ({ method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(b) });

export const TriggerSchedule = z.union([
  z.object({ kind: z.literal('cron'), expr: z.string() }).passthrough(),
  z.object({ kind: z.literal('interval'), minutes: z.number() }).passthrough(),
]);
export type TriggerScheduleInput = { kind: 'cron'; expr: string } | { kind: 'interval'; minutes: number };

export const PausedReason = z.enum(['needs_consent', 'failures', 'target_missing', 'owner_inactive']);

export const TriggerView = z.object({
  id: z.string(),
  nameHe: z.string(),
  slug: z.string(),
  artifactNameHe: z.string().nullable(),
  params: z.record(z.string(), z.unknown()).default({}),
  /** artifacts-report-lookup: התוויות של שדות בחירה עם חיפוש ("לקוח: <תווית>"). */
  paramLabels: z.record(z.string(), z.string()).nullable().optional(),
  schedule: TriggerSchedule.nullable(),
  describeHe: z.string(),
  enabled: z.boolean(),
  pausedReason: PausedReason.nullable(),
  pausedHe: z.string().nullable(),
  lastRunAt: z.string().nullable(),
  lastStatus: z.string().nullable(),
  lastRunId: z.string().nullable(),
  lastErrorHe: z.string().nullable(),
  nextRunAt: z.string().nullable(),
  owner: z.object({ id: z.string(), nameHe: z.string() }),
  createdAt: z.string().nullable(),
  /** triggers-failure-notify-retry: ניסיון חוזר ממתין ("ינסה שוב ב-HH:MM", מהשרת). */
  retryAt: z.string().nullable().optional(),
  retryHe: z.string().nullable().optional(),
});
export type TriggerView = z.infer<typeof TriggerView>;

export const TriggerWarning = z.object({ code: z.string(), interfaceId: z.string(), textHe: z.string() });
export type TriggerWarning = z.infer<typeof TriggerWarning>;

const TriggerList = z.object({ triggers: z.array(TriggerView) });
const TriggerSaved = z.object({ trigger: TriggerView, warnings: z.array(TriggerWarning).default([]), summaryHe: z.string().default('') });
const RunNow = z.object({ triggerRunId: z.string(), runId: z.string().nullable(), status: z.string(), summaryHe: z.string().default('') });
const TriggerRuns = z.object({
  runs: z.array(
    z.object({
      id: z.string(),
      scheduledFor: z.string().nullable(),
      firedAt: z.string().nullable(),
      status: z.string(),
      artifactRunId: z.string().nullable(),
      reason: z.string().nullable(),
      errorHe: z.string().nullable(),
      finishedAt: z.string().nullable(),
      msTotal: z.number().nullable(),
      attempt: z.number().default(1),
    }),
  ),
});

export type CreateTriggerInput = { nameHe: string; slug: string; params?: Record<string, unknown>; paramLabels?: Record<string, string>; schedule: TriggerScheduleInput; enabled?: boolean };

export const triggersApi = {
  forArtifact: (slug: string) => json(TriggerList, `/api/artifacts/${enc(slug)}/triggers`),
  create: (input: CreateTriggerInput) => json(TriggerSaved, '/api/triggers', body('POST', input)),
  update: (id: string, patch: Partial<Omit<CreateTriggerInput, 'slug'>>) => json(TriggerSaved, `/api/triggers/${enc(id)}`, body('PATCH', patch)),
  pause: (id: string) => json(TriggerSaved, `/api/triggers/${enc(id)}/pause`, body('POST', {})),
  resume: (id: string) => json(TriggerSaved, `/api/triggers/${enc(id)}/resume`, body('POST', {})),
  run: (id: string) => json(RunNow, `/api/triggers/${enc(id)}/run`, body('POST', {})),
  remove: (id: string) => authed(`/api/triggers/${enc(id)}`, { method: 'DELETE' }).then(() => undefined),
  runs: (id: string) => json(TriggerRuns, `/api/triggers/${enc(id)}/runs`),
};

/** בחירה מוכנה → cron (שעון ישראל; השרת מוסיף tz). */
export type Preset = 'daily' | 'workdays' | 'weekly' | 'monthly' | 'advanced';
export function presetCron(p: Exclude<Preset, 'advanced'>, time: string, weekday: number, dom: number): string {
  const [h, m] = time.split(':').map((x) => Number(x));
  const hm = `${Number.isFinite(m) ? m : 0} ${Number.isFinite(h) ? h : 0}`;
  switch (p) {
    case 'daily':
      return `${hm} * * *`;
    case 'workdays':
      return `${hm} * * 0-4`;
    case 'weekly':
      return `${hm} * * ${weekday}`;
    case 'monthly':
      return `${hm} ${dom} * *`;
  }
}
